import apiClient from './client';
import { citiesApi } from './citiesApi.ts';

const DEFAULT_TTL = 2 * 60 * 1000;
const STATIC_TTL = 10 * 60 * 1000;
const LIST_TTL = 30 * 1000;
const cache = new Map();

const scopedKey = (key) => `${localStorage.getItem('accessToken') || 'guest'}:${key}`;

export const getCachedRequest = (key, loader, ttl = DEFAULT_TTL) => {
  const storageKey = scopedKey(key);
  const now = Date.now();
  const cached = cache.get(storageKey);

  if (cached?.data !== undefined && cached.expiresAt > now) {
    return Promise.resolve(cached.data);
  }

  if (cached?.promise) return cached.promise;

  const promise = loader()
    .then((data) => {
      cache.set(storageKey, { data, expiresAt: Date.now() + ttl });
      return data;
    })
    .catch((error) => {
      cache.delete(storageKey);
      throw error;
    });

  cache.set(storageKey, { promise });
  return promise;
};

export const invalidateFrontendCache = (...keys) => {
  keys.forEach((key) => cache.delete(scopedKey(key)));
};

export const invalidateFrontendCacheByPrefix = (keyPrefix) => {
  const prefix = scopedKey(keyPrefix);
  [...cache.keys()].forEach((key) => {
    if (key.startsWith(prefix)) cache.delete(key);
  });
};

export const updateFrontendCache = (key, updater) => {
  const storageKey = scopedKey(key);
  const cached = cache.get(storageKey);
  if (cached?.data === undefined) return;

  const data = typeof updater === 'function' ? updater(cached.data) : updater;
  cache.set(storageKey, { data, expiresAt: Date.now() + DEFAULT_TTL });
};

export const getCachedServiceCatalog = () =>
  getCachedRequest(
    'service-catalog',
    () => apiClient.get('/public/services/catalog').then((response) => response.data || []),
    STATIC_TTL
  );

export const getCachedDetailedServiceCatalog = () =>
  getCachedRequest(
    'service-catalog-detailed',
    () => apiClient.get('/public/services/catalog', {
      params: { includeUnits: true, includeServices: true },
    }).then((response) => response.data || []),
    STATIC_TTL
  );

export const getCachedServiceUnits = () =>
  getCachedRequest(
    'service-units',
    () => apiClient.get('/public/services/units').then((response) => response.data || []),
    STATIC_TTL
  );

export const getCachedCities = () =>
  getCachedRequest('cities', () => citiesApi.getAllCities(), STATIC_TTL);

export const getCachedCountries = () =>
  getCachedRequest(
    'countries',
    () => apiClient.get('/geo/cities/countries').then((response) => response.data || []),
    STATIC_TTL
  );

export const getCachedPrivacyPolicy = () =>
  getCachedRequest(
    'privacy-policy',
    () => apiClient.get('/legal/privacy-policy').then((response) => response.data),
    STATIC_TTL
  );

export const getCachedCustomerOrder = (orderId) =>
  getCachedRequest(`customer-order-${orderId}`, () =>
    apiClient.get(`/customers/me/orders/${orderId}`)
      .then((response) => response.data.order || response.data)
  );

export const getCachedOrderDetail = (role, orderId) => {
  const isCustomer = role === 'CUSTOMER';
  const url = isCustomer
    ? `/customers/me/orders/${orderId}?incViews=true`
    : `/executors/orders/${orderId}?incViews=true`;

  return getCachedRequest(`order-detail-${role}-${orderId}`, () =>
    apiClient.get(url).then((response) => ({
      order: isCustomer ? (response.data.order || response.data) : response.data,
      chats: isCustomer ? (response.data.chats || []) : [],
    }))
  );
};

export const getCachedExecutorProfile = () =>
  getCachedRequest('executor-profile', () =>
    apiClient.get('/executors/me/profile').then((response) => response.data)
  );

export const getCachedCustomerProfile = () =>
  getCachedRequest('customer-profile', () =>
    apiClient.get('/customers/me/profile').then((response) => response.data)
  );

export const getCachedCurrentUser = () =>
  getCachedRequest('current-user', () =>
    apiClient.get('/users/me').then((response) => response.data)
  );

export const getCachedComplaintCategories = () =>
  getCachedRequest(
    'support-ticket-categories',
    () => apiClient.get('/support/tickets/categories').then((response) => response.data || []),
    STATIC_TTL
  );

export const getCachedProtectedBlob = (url) =>
  getCachedRequest(
    `protected-blob-${url}`,
    () => apiClient.get(url, { responseType: 'blob' }).then((response) => response.data),
    LIST_TTL
  );

export const getCachedExecutorInvitations = () =>
  getCachedRequest('executor-invitations-sent', () =>
    apiClient.get('/executors/me/invitations', { params: { status: 'SENT' } })
      .then((response) => response.data || [])
  );

export const getCachedExecutorResponses = () =>
  getCachedRequest('executor-order-responses', () =>
    apiClient.get('/executors/me/order-responses', { params: { page: 0, size: 100 } })
      .then((response) => response.data.items || [])
  );

export const getCachedExecutorFavoriteOrders = () =>
  getCachedRequest('executor-favorite-orders', () =>
    apiClient.get('/executors/me/favorite-orders', { params: { page: 0, size: 200 } })
      .then((response) => response.data.items || [])
  );

export const getCachedExecutorFavoriteOrdersPage = (page, size) =>
  getCachedRequest(
    `executor-favorite-orders-page-${page}-${size}`,
    () => apiClient.get('/executors/me/favorite-orders', { params: { page, size } })
      .then((response) => response.data),
    LIST_TTL
  );

export const getCachedExecutorOrderPreview = (orderId) =>
  getCachedRequest(
    `executor-order-preview-${orderId}`,
    () => apiClient.get(`/executors/orders/${orderId}`, { params: { incViews: false } })
      .then((response) => response.data),
    LIST_TTL
  );

export const getCachedExecutorProjects = () =>
  getCachedRequest(
    'executor-projects',
    () => apiClient.get('/executors/me/projects').then((response) => response.data || []),
    LIST_TTL
  );

export const getCachedExecutorProjectFiles = (projectId, type) =>
  getCachedRequest(
    `executor-project-${projectId}-${type}`,
    () => apiClient.get(`/executors/me/projects/${projectId}/${type}`)
      .then((response) => response.data || []),
    LIST_TTL
  );

const stableParamsKey = (params) => Object.keys(params)
  .sort()
  .map((key) => `${key}:${Array.isArray(params[key]) ? params[key].join(',') : params[key]}`)
  .join('|');

export const getCachedExecutorOrders = (params) =>
  getCachedRequest(
    `executor-orders-${stableParamsKey(params)}`,
    () => apiClient.get('/executors/orders', { params }).then((response) => response.data),
    LIST_TTL
  );

export const getCachedExecutorMyOrders = (params) =>
  getCachedRequest(
    `executor-my-orders-${stableParamsKey(params)}`,
    () => apiClient.get('/executors/me/my-orders', { params }).then((response) => response.data),
    LIST_TTL
  );

export const getCachedPublicExecutors = (params) =>
  getCachedRequest(`public-executors-${stableParamsKey(params)}`, () =>
    apiClient.get('/public/executors', { params })
      .then((response) => response.data?.items || [])
  );
